import React, { useState, useEffect, useMemo } from 'react';
import { Panel, Button, Badge } from '../../design/primitives';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../../design/tokens';
import { apiClient } from '../../api/apiConfig';
import { MetricChart, Spinner } from '../ui';
import { GroupedAccountsResponse, NetWorthHistoryPoint, FinancialAccount, BusinessValuation } from '../../types/finance';

const formatCurrency = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

const formatCompact = (n: number) => {
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toFixed(0);
};

const PERIODS = [
  { label: '1M', months: 1 },
  { label: '3M', months: 3 },
  { label: '6M', months: 6 },
  { label: '1Y', months: 12 },
  { label: 'All', months: 60 },
];

const GROUP_LABELS: Record<string, { label: string; icon: string }> = {
  investments: { label: 'Investments', icon: '📈' },
  cash: { label: 'Cash', icon: '💵' },
  credit_cards: { label: 'Credit Cards', icon: '💳' },
  real_estate: { label: 'Real Estate', icon: '🏠' },
  vehicles: { label: 'Vehicles', icon: '🚗' },
  businesses: { label: 'Businesses', icon: '💼' },
  other_liabilities: { label: 'Other Liabilities', icon: '📋' },
};

interface Driver {
  name: string;
  change: number;
  nw_impact: number;
  is_asset: boolean;
}

interface PeriodChange {
  change: number;
  pct: number;
  ref_date: string;
  ref_nw: number;
  drivers: Driver[];
}

interface ChangesData {
  current_net_worth: number;
  date: string;
  daily: PeriodChange | null;
  mtd: PeriodChange | null;
  ytd: PeriodChange | null;
}

const AccountRow: React.FC<{ account: FinancialAccount }> = ({ account }) => (
  <div style={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: `${SPACING[8]} ${SPACING[12]}`,
    borderBottom: `1px solid ${COLORS.border.default}`,
    fontSize: TYPOGRAPHY.body.sizes.default,
    fontFamily: TYPOGRAPHY.body.fontFamily,
  }}>
    <div>
      <div style={{ color: COLORS.text.primary, fontWeight: '500' }}>{account.name}</div>
      <div style={{ fontSize: TYPOGRAPHY.body.sizes.tiny, color: COLORS.text.tertiary }}>
        {account.institution_name || account.account_type_display}
      </div>
    </div>
    <div style={{
      fontWeight: '600',
      color: account.is_asset ? COLORS.text.primary : COLORS.status.error,
    }}>
      {formatCurrency(account.current_balance)}
    </div>
  </div>
);

const BusinessRow: React.FC<{ biz: BusinessValuation }> = ({ biz }) => (
  <div style={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: `${SPACING[8]} ${SPACING[12]}`,
    borderBottom: `1px solid ${COLORS.border.default}`,
    fontSize: TYPOGRAPHY.body.sizes.default,
    fontFamily: TYPOGRAPHY.body.fontFamily,
  }}>
    <div>
      <div style={{ color: COLORS.text.primary, fontWeight: '500' }}>{biz.name}</div>
      <div style={{ fontSize: TYPOGRAPHY.body.sizes.tiny, color: COLORS.text.tertiary }}>
        {biz.multiple}x SDE &middot; {biz.growth_stage}
      </div>
    </div>
    <div style={{ fontWeight: '600', color: COLORS.text.primary }}>
      {formatCurrency(biz.valuation)}
    </div>
  </div>
);

const BusinessGroupSection: React.FC<{
  items: BusinessValuation[];
  total: number;
}> = ({ items, total }) => {
  if (items.length === 0) return null;
  return (
    <Panel style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: `${SPACING[8]} ${SPACING[12]}`,
        backgroundColor: COLORS.bg.hover,
        borderBottom: `1px solid ${COLORS.border.default}`,
      }}>
        <div style={{
          fontFamily: TYPOGRAPHY.headings.fontFamily,
          fontSize: TYPOGRAPHY.headings.sizes.h3,
          fontWeight: TYPOGRAPHY.headings.weight,
          color: COLORS.text.primary,
        }}>
          Businesses
        </div>
        <div style={{
          fontFamily: TYPOGRAPHY.headings.fontFamily,
          fontSize: TYPOGRAPHY.headings.sizes.h3,
          fontWeight: '700',
          color: COLORS.text.primary,
        }}>
          {formatCurrency(total)}
        </div>
      </div>
      {items.map(b => <BusinessRow key={b.id} biz={b} />)}
    </Panel>
  );
};

const AccountGroupSection: React.FC<{
  groupKey: string;
  accounts: FinancialAccount[];
  total: number;
}> = ({ groupKey, accounts, total }) => {
  const info = GROUP_LABELS[groupKey] || { label: groupKey, icon: '📦' };
  if (accounts.length === 0) return null;

  return (
    <Panel style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: `${SPACING[8]} ${SPACING[12]}`,
        backgroundColor: COLORS.bg.hover,
        borderBottom: `1px solid ${COLORS.border.default}`,
      }}>
        <div style={{
          fontFamily: TYPOGRAPHY.headings.fontFamily,
          fontSize: TYPOGRAPHY.headings.sizes.h3,
          fontWeight: TYPOGRAPHY.headings.weight,
          color: COLORS.text.primary,
        }}>
          {info.label}
        </div>
        <div style={{
          fontFamily: TYPOGRAPHY.headings.fontFamily,
          fontSize: TYPOGRAPHY.headings.sizes.h3,
          fontWeight: '700',
          color: COLORS.text.primary,
        }}>
          {formatCurrency(total)}
        </div>
      </div>
      {accounts.map(a => <AccountRow key={a.id} account={a} />)}
    </Panel>
  );
};

interface LiquidityTier {
  label: string;
  value: number;
  color: string;
}

const LiquidityBreakdown: React.FC<{ grouped: GroupedAccountsResponse }> = ({ grouped }) => {
  const breakdown = useMemo(() => {
    const bal = (a: FinancialAccount) => Number(a.current_balance) || 0;

    // Tier 1: Cash & Equivalents
    const cashTotal = Number(grouped.cash.total) || 0;

    // Tier 2: Liquid Investments (non-retirement)
    const liquidInvestments = grouped.investments.accounts
      .filter(a => a.account_type !== 'retirement')
      .reduce((sum, a) => sum + bal(a), 0);

    // Tier 3: Retirement
    const retirement = grouped.investments.accounts
      .filter(a => a.account_type === 'retirement')
      .reduce((sum, a) => sum + bal(a), 0);

    // Tier 4: Illiquid Assets (net of secured debt via linked_loan equity)
    const realEstateEquity = grouped.real_estate.accounts
      .reduce((sum, a) => sum + (Number(a.linked_loan?.equity) || bal(a)), 0);
    const vehicleEquity = grouped.vehicles.accounts
      .reduce((sum, a) => sum + (Number(a.linked_loan?.equity) || bal(a)), 0);
    const illiquidTotal = realEstateEquity + vehicleEquity + (Number(grouped.businesses.total) || 0);

    // Unsecured Liabilities (exclude secured debts already netted above)
    const unsecuredLiabilities =
      grouped.credit_cards.accounts
        .filter(a => !a.linked_asset)
        .reduce((sum, a) => sum + bal(a), 0) +
      grouped.other_liabilities.accounts
        .filter(a => !a.linked_asset)
        .reduce((sum, a) => sum + bal(a), 0);

    const tiers: LiquidityTier[] = [
      { label: 'Cash & Equivalents', value: cashTotal, color: COLORS.status.success },
      { label: 'Liquid Investments', value: liquidInvestments, color: COLORS.accent.default },
      { label: 'Retirement', value: retirement, color: COLORS.status.info },
      { label: 'Illiquid Assets', value: illiquidTotal, color: COLORS.status.warning },
    ];

    const totalPositive = tiers.reduce((sum, t) => sum + Math.max(0, t.value), 0);

    return { tiers, unsecuredLiabilities, totalPositive };
  }, [grouped]);

  const { tiers, unsecuredLiabilities, totalPositive } = breakdown;
  const visibleTiers = tiers.filter(t => t.value > 0);

  return (
    <Panel>
      <div style={{
        fontFamily: TYPOGRAPHY.headings.fontFamily,
        fontSize: TYPOGRAPHY.headings.sizes.h3,
        fontWeight: TYPOGRAPHY.headings.weight,
        color: COLORS.text.primary,
        marginBottom: SPACING[12],
      }}>
        Liquidity Breakdown
      </div>

      {/* Stacked bar */}
      {totalPositive > 0 && (
        <div style={{
          display: 'flex',
          height: '12px',
          borderRadius: RADIUS,
          overflow: 'hidden',
          marginBottom: SPACING[16],
        }}>
          {visibleTiers.map(tier => (
            <div
              key={tier.label}
              style={{
                width: `${(tier.value / totalPositive) * 100}%`,
                backgroundColor: tier.color,
                minWidth: '2px',
              }}
            />
          ))}
        </div>
      )}

      {/* Legend rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[8] }}>
        {tiers.map(tier => (
          <div key={tier.label} style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: TYPOGRAPHY.body.sizes.default,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: tier.color,
                flexShrink: 0,
              }} />
              <span style={{ color: COLORS.text.secondary }}>{tier.label}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[12] }}>
              <span style={{ fontWeight: '600', color: COLORS.text.primary }}>
                {formatCurrency(tier.value)}
              </span>
              {totalPositive > 0 && (
                <span style={{
                  color: COLORS.text.tertiary,
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  width: '40px',
                  textAlign: 'right',
                }}>
                  {((tier.value / totalPositive) * 100).toFixed(1)}%
                </span>
              )}
            </div>
          </div>
        ))}

        {unsecuredLiabilities !== 0 && (
          <>
            <div style={{ height: '1px', backgroundColor: COLORS.border.default }} />
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontSize: TYPOGRAPHY.body.sizes.default,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: COLORS.status.error,
                  flexShrink: 0,
                }} />
                <span style={{ color: COLORS.text.secondary }}>Unsecured Liabilities</span>
              </div>
              <span style={{ fontWeight: '600', color: COLORS.status.error }}>
                {formatCurrency(-Math.abs(unsecuredLiabilities))}
              </span>
            </div>
          </>
        )}
      </div>
    </Panel>
  );
};

const ChangeCard: React.FC<{ label: string; period: PeriodChange | null }> = ({ label, period }) => {
  if (!period) {
    return (
      <div style={{
        flex: 1,
        padding: SPACING[12],
        backgroundColor: COLORS.bg.panel,
        borderRadius: RADIUS,
        border: `1px solid ${COLORS.border.default}`,
      }}>
        <div style={{ fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.tertiary, marginBottom: SPACING[4] }}>
          {label}
        </div>
        <div style={{ fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.tertiary }}>
          --
        </div>
      </div>
    );
  }

  const isPositive = period.change >= 0;
  const color = isPositive ? COLORS.status.success : COLORS.status.error;
  const arrow = isPositive ? '\u25B2' : '\u25BC';

  return (
    <div style={{
      flex: 1,
      padding: SPACING[12],
      backgroundColor: COLORS.bg.panel,
      borderRadius: RADIUS,
      border: `1px solid ${COLORS.border.default}`,
    }}>
      <div style={{ fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.tertiary, marginBottom: SPACING[4] }}>
        {label}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: SPACING[8] }}>
        <span style={{
          fontFamily: TYPOGRAPHY.headings.fontFamily,
          fontSize: TYPOGRAPHY.headings.sizes.h3,
          fontWeight: '700',
          color,
        }}>
          {arrow} {formatCurrency(Math.abs(period.change))}
        </span>
        <span style={{ fontSize: TYPOGRAPHY.body.sizes.small, color, fontWeight: '600' }}>
          {isPositive ? '+' : ''}{period.pct.toFixed(2)}%
        </span>
      </div>
    </div>
  );
};

const DriversSection: React.FC<{ changes: ChangesData }> = ({ changes }) => {
  // Show drivers from the most granular available period
  const period = changes.daily || changes.mtd || changes.ytd;
  if (!period || period.drivers.length === 0) return null;

  const periodLabel = changes.daily ? 'Today' : changes.mtd ? 'This Month' : 'This Year';

  return (
    <Panel>
      <div style={{
        fontFamily: TYPOGRAPHY.headings.fontFamily,
        fontSize: TYPOGRAPHY.headings.sizes.h3,
        fontWeight: TYPOGRAPHY.headings.weight,
        color: COLORS.text.primary,
        marginBottom: SPACING[12],
      }}>
        Main Drivers ({periodLabel})
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[4] }}>
        {period.drivers.map((d, i) => {
          const isPositive = d.nw_impact >= 0;
          const color = isPositive ? COLORS.status.success : COLORS.status.error;
          return (
            <div key={i} style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: `${SPACING[8]} 0`,
              borderBottom: i < period.drivers.length - 1 ? `1px solid ${COLORS.border.default}` : 'none',
              fontSize: TYPOGRAPHY.body.sizes.default,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
                <div style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  backgroundColor: color,
                  flexShrink: 0,
                }} />
                <span style={{ color: COLORS.text.primary }}>{d.name}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[12] }}>
                <span style={{ fontWeight: '600', color, fontSize: TYPOGRAPHY.body.sizes.small }}>
                  {isPositive ? '+' : ''}{formatCurrency(d.nw_impact)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </Panel>
  );
};

const NetWorthView: React.FC = () => {
  const [history, setHistory] = useState<NetWorthHistoryPoint[]>([]);
  const [grouped, setGrouped] = useState<GroupedAccountsResponse | null>(null);
  const [changes, setChanges] = useState<ChangesData | null>(null);
  const [loading, setLoading] = useState(false);
  const [months, setMonths] = useState(12);

  const fetchData = async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    try {
      const [histRes, grpRes, chgRes] = await Promise.all([
        apiClient.get(`/api/net-worth/history/?months=${months}`),
        apiClient.get('/api/financial-accounts/grouped/'),
        apiClient.get('/api/net-worth/changes/'),
      ]);
      setHistory(histRes.data);
      setGrouped(grpRes.data);
      setChanges(chgRes.data);
    } catch (err) {
      console.error('Failed to fetch net worth data', err);
    } finally {
      if (showSpinner) setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(true);
    // Auto-refresh every 60 seconds without showing spinner
    const interval = setInterval(() => fetchData(false), 60_000);
    return () => clearInterval(interval);
  }, [months]);

  const handleSnapshot = async () => {
    try {
      await apiClient.post('/api/net-worth/snapshot_now/');
      await fetchData(false);
    } catch (err) {
      console.error('Failed to take snapshot', err);
    }
  };

  const chartData = useMemo(() => {
    if (history.length === 0) return [];
    const dates = history.map(h => new Date(h.date));
    const first = dates[0];
    const last = dates[dates.length - 1];
    const spanDays = (last.getTime() - first.getTime()) / (1000 * 60 * 60 * 24);

    return history.map(h => {
      const d = new Date(h.date);
      let label: string;
      if (spanDays <= 60) {
        // Within ~2 months: show "Feb 5", "Feb 12"
        label = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      } else if (spanDays <= 365) {
        // Within a year: show "Feb '26"
        label = d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      } else {
        // Multi-year: show "Feb 2026"
        label = d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      }
      return {
        date: label,
        net_worth: h.net_worth,
        total_assets: h.total_assets,
        total_liabilities: h.total_liabilities,
      };
    });
  }, [history]);

  const currentNW = grouped?.summary.net_worth ?? 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{
            fontFamily: TYPOGRAPHY.headings.fontFamily,
            fontSize: TYPOGRAPHY.headings.sizes.h1,
            fontWeight: '700',
            color: COLORS.text.primary,
          }}>
            {formatCurrency(currentNW)}
          </div>
          <div style={{ fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.secondary }}>
            Current Net Worth
          </div>
        </div>
        <div style={{ display: 'flex', gap: SPACING[8], alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: SPACING[4] }}>
            {PERIODS.map(p => (
              <Button
                key={p.months}
                variant={months === p.months ? 'primary' : 'ghost'}
                onClick={() => setMonths(p.months)}
                style={{ padding: `${SPACING[4]} ${SPACING[12]}` }}
              >
                {p.label}
              </Button>
            ))}
          </div>
          <Button variant="secondary" onClick={handleSnapshot}>
            Snapshot Now
          </Button>
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: SPACING[32] }}>
          <Spinner size="md" />
        </div>
      ) : (
        <>
          {/* Daily / MTD / YTD Cards */}
          {changes && (
            <div style={{ display: 'flex', gap: SPACING[12] }}>
              <ChangeCard label="Daily" period={changes.daily} />
              <ChangeCard label="MTD" period={changes.mtd} />
              <ChangeCard label="YTD" period={changes.ytd} />
            </div>
          )}

          {/* Net Worth Chart */}
          {chartData.length > 0 && (
            <Panel>
              <div style={{
                fontFamily: TYPOGRAPHY.headings.fontFamily,
                fontSize: TYPOGRAPHY.headings.sizes.h3,
                fontWeight: TYPOGRAPHY.headings.weight,
                color: COLORS.text.primary,
                marginBottom: SPACING[8],
              }}>
                Net Worth
              </div>
              <MetricChart
                data={chartData}
                series={[
                  { dataKey: 'net_worth', name: 'Net Worth', metricType: 'currency', color: COLORS.accent.default },
                ]}
                type="line"
                height={250}
              />
            </Panel>
          )}

          {/* Assets & Liabilities Chart */}
          {chartData.length > 0 && (
            <Panel>
              <div style={{
                fontFamily: TYPOGRAPHY.headings.fontFamily,
                fontSize: TYPOGRAPHY.headings.sizes.h3,
                fontWeight: TYPOGRAPHY.headings.weight,
                color: COLORS.text.primary,
                marginBottom: SPACING[8],
              }}>
                Assets & Liabilities
              </div>
              <MetricChart
                data={chartData}
                series={[
                  { dataKey: 'total_assets', name: 'Assets', metricType: 'currency', color: COLORS.status.success },
                  { dataKey: 'total_liabilities', name: 'Liabilities', metricType: 'currency', color: COLORS.status.error },
                ]}
                type="line"
                height={250}
              />
            </Panel>
          )}

          {/* Main Drivers */}
          {changes && <DriversSection changes={changes} />}

          {/* Liquidity Breakdown */}
          {grouped && <LiquidityBreakdown grouped={grouped} />}

          {/* Account groups */}
          {grouped && (
            <>
              {(['investments', 'cash', 'real_estate', 'vehicles', 'credit_cards', 'other_liabilities'] as const).map(key => {
                const group = grouped[key];
                if (!group) return null;
                return (
                  <AccountGroupSection
                    key={key}
                    groupKey={key}
                    accounts={group.accounts}
                    total={group.total}
                  />
                );
              })}
              {grouped.businesses && grouped.businesses.items.length > 0 && (
                <BusinessGroupSection
                  items={grouped.businesses.items}
                  total={grouped.businesses.total}
                />
              )}

              {/* Summary */}
              <Panel>
                <div style={{
                  fontFamily: TYPOGRAPHY.headings.fontFamily,
                  fontSize: TYPOGRAPHY.headings.sizes.h3,
                  fontWeight: TYPOGRAPHY.headings.weight,
                  color: COLORS.text.primary,
                  marginBottom: SPACING[12],
                }}>
                  Summary
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[8] }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: TYPOGRAPHY.body.sizes.default }}>
                    <span style={{ color: COLORS.text.secondary }}>Total Assets</span>
                    <span style={{ fontWeight: '600', color: COLORS.text.primary }}>{formatCurrency(grouped.summary.total_assets)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: TYPOGRAPHY.body.sizes.default }}>
                    <span style={{ color: COLORS.text.secondary }}>Total Liabilities</span>
                    <span style={{ fontWeight: '600', color: COLORS.status.error }}>{formatCurrency(grouped.summary.total_liabilities)}</span>
                  </div>
                  <div style={{ height: '1px', backgroundColor: COLORS.border.default }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: TYPOGRAPHY.body.sizes.default }}>
                    <span style={{ fontWeight: '600', color: COLORS.text.primary }}>Net Worth</span>
                    <span style={{
                      fontWeight: '700',
                      color: grouped.summary.net_worth >= 0 ? COLORS.status.success : COLORS.status.error,
                    }}>
                      {formatCurrency(grouped.summary.net_worth)}
                    </span>
                  </div>
                </div>
              </Panel>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default NetWorthView;
